import { useRouter } from "@tanstack/react-router"
import type { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community"
import type { AgGridReact } from "ag-grid-react"
import { useCallback, useMemo, useRef } from "react"

import { WorkspacePage } from "~/components/design-system/pages"
import {
  CategoryCell,
  ReviewDate,
  useFlagFilters,
} from "~/features/feature-flags/client/FlagControls"
import { useFlagPanel } from "~/features/feature-flags/client/FlagPanelProvider"
import { FlagStatusCell } from "~/features/feature-flags/client/FlagStatusBadge"
import { LaunchDarklyImportButton } from "~/features/feature-flags/client/LaunchDarklyImportButton"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import { toUTCDateString } from "~/utils/datetime"
import { openInNewTab } from "~/utils/routerUtils"
import { matchesFlag, NO_MATCHES } from "~/features/feature-flags/client/model/filters"
import {
  CATEGORY_LABELS,
  flagReview,
  type FlagReview,
} from "~/features/feature-flags/client/model/labels"
import {
  FEATURE_FLAGS,
  FLAG_KEYS_BY_CATEGORY,
  type FeatureFlagKey,
  type FlagDefinition,
} from "~/features/feature-flags/shared/registry"
import { lockedByLaunchDarkly } from "~/features/feature-flags/shared/changeRules"
import {
  FLAG_STATE_ORDER,
  flagStatus,
  type FlagStatus,
} from "~/features/feature-flags/client/model/status"

type FlagRow = {
  key: FeatureFlagKey
  definition: FlagDefinition
  status: FlagStatus
  review: FlagReview | null
  source: "LaunchDarkly" | "Hakio"
}

const DEFAULT_COLUMN_DEF: ColDef<FlagRow> = {
  suppressHeaderMenuButton: true,
  sortable: true,
  cellClass: "ag-cell-vcenter",
}

const compareStatus = (a?: FlagStatus, b?: FlagStatus) =>
  FLAG_STATE_ORDER[a?.state ?? "off"] - FLAG_STATE_ORDER[b?.state ?? "off"] ||
  (a?.companiesOn ?? 0) - (b?.companiesOn ?? 0)

/** By date, flags without a review date last. */
const compareReview = (a?: FlagReview | null, b?: FlagReview | null) => {
  if (!a || !b) return Number(!a) - Number(!b)
  return a.date.localeCompare(b.date)
}

/** Opens a flag's page; ⌘/Ctrl-click opens it in a new tab. */
function useOpenFlagPage() {
  const router = useRouter()
  return useCallback(
    (flagKey: FeatureFlagKey, event?: Event | null) => {
      const location = { to: "/internal/feature-flags/$flagKey", params: { flagKey } } as const
      const modified = event instanceof MouseEvent && (event.metaKey || event.ctrlKey)
      if (modified) openInNewTab(router.buildLocation(location).href)
      else void router.navigate(location)
    },
    [router],
  )
}

function FlagNameCell({ data }: ICellRendererParams<FlagRow>) {
  if (!data) return null
  return (
    <span className="flex h-full w-full min-w-0 items-center gap-2">
      {/* The name keeps its width, so a narrow column cuts the key before the name. */}
      <span className="max-w-full shrink-0 truncate label-base text-fg-base">
        {data.definition.name}
      </span>
      <span className="min-w-0 truncate paragraph-sm font-mono text-fg-muted">{data.key}</span>
    </span>
  )
}

function ReviewCell({ data }: ICellRendererParams<FlagRow>) {
  return data?.review ? <ReviewDate review={data.review} /> : null
}

const COLUMN_DEFS: ColDef<FlagRow>[] = [
  {
    colId: "name",
    headerName: "Flag",
    valueGetter: ({ data }) => data?.definition.name,
    cellRenderer: FlagNameCell,
    tooltip: ({ data }) => data?.definition.description,
    // Cell text selection wraps the value, and the wrapper sizes to its content: stretch it, and
    // the value across it (`ag-cell-vcenter` gives the value its full height), so the name
    // truncates and the key stays inside the column.
    cellClass:
      "ag-cell-vcenter [&_.ag-cell-wrapper]:size-full [&_.ag-cell-wrapper]:min-w-0 [&_.ag-cell-value]:w-full",
    pinned: "left",
    width: 280,
    suppressSizeToFit: true,
  },
  {
    colId: "category",
    headerName: "Category",
    valueGetter: ({ data }) => (data ? CATEGORY_LABELS[data.definition.category] : ""),
    cellRenderer: CategoryCell,
    minWidth: 100,
    flex: 0.7,
  },
  {
    colId: "source",
    headerName: "Source",
    headerTooltip:
      "LaunchDarkly: synced from LaunchDarkly and locked here until the switch is on. Hakio: exists only here.",
    field: "source",
    minWidth: 120,
    flex: 0.7,
  },
  {
    colId: "status",
    headerName: "Status",
    headerTooltip: "Status for every company. Partial means some companies differ.",
    valueGetter: ({ data }) => data?.status,
    comparator: compareStatus,
    cellRenderer: FlagStatusCell,
    // Fits a Partial badge with its count.
    minWidth: 144,
    flex: 1,
  },
  {
    colId: "review",
    headerName: "Review by",
    headerTooltip: "When a custom flag is due for review: remove it, extend it or promote it.",
    valueGetter: ({ data }) => data?.review,
    comparator: compareReview,
    cellRenderer: ReviewCell,
    minWidth: 140,
    flex: 1,
  },
]

/** Every flag with its category, status and review date. A row opens the flag's page. */
export function FlagsTab() {
  const panel = useFlagPanel()
  const gridRef = useRef<AgGridReact<FlagRow>>(null)
  const { category, search, toolbar } = useFlagFilters({ placeholder: "Search flags" })
  const openFlagPage = useOpenFlagPage()

  const allRows = useMemo<FlagRow[]>(() => {
    const today = toUTCDateString(new Date())
    return FLAG_KEYS_BY_CATEGORY.map((key) => ({
      key,
      definition: FEATURE_FLAGS[key],
      status: flagStatus(panel, key),
      review: flagReview(FEATURE_FLAGS[key], today),
      source: lockedByLaunchDarkly({ flagKey: key, scope: "default" }, panel.lock)
        ? "LaunchDarkly"
        : "Hakio",
    }))
  }, [panel])

  const rows = useMemo(
    () => allRows.filter(({ key, definition }) => matchesFlag(key, definition, category, search)),
    [allRows, category, search],
  )

  const gridOptions = useMemo<GridOptions<FlagRow>>(
    () => ({
      onRowClicked: ({ data, event }) => {
        if (data) openFlagPage(data.key, event)
      },
    }),
    [openFlagPage],
  )

  return (
    <WorkspacePage
      toolbar={
        <div className="flex w-full min-w-0 flex-wrap items-center gap-3">
          {toolbar}
          <LaunchDarklyImportButton />
        </div>
      }
    >
      <DataTableBaseV2<FlagRow>
        id="feature-flags-by-flag"
        gridRef={gridRef}
        rowData={rows}
        rowIdField="key"
        columnDefs={COLUMN_DEFS}
        defaultColumnDef={DEFAULT_COLUMN_DEF}
        gridOptions={gridOptions}
        loading={panel.loadingCompanies}
        showPagination={false}
        disableColumnSizingPersistence
        emptyMessage={NO_MATCHES}
      />
    </WorkspacePage>
  )
}
